/**
 * Phase 4 + Phase 7A — Cost Center monthly renewal approval sheet.
 *
 * Renewal flow is Finance-only (post-Phase 7A). After the monthly close
 * recalibrates a CC's monthly_estimate, the CC sits in "Pending Renewal" and
 * any Finance user can approve. Finance can optionally **override** the
 * recalibrated number via the "Modify estimate" input (approve-with-changes)
 * before clicking Approve.
 *
 * Visual: shows the recal summary up top, then a single-step "Finance" stepper
 * and an action row with Approve / Reject buttons.
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import StepperWidget from "../../../../ui/new_components/common/stepper_widget";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../utils/enums";
import {
  CCMonthStatus,
  CostCenter,
  MasterCostCenterStatus,
  RenewalHistoryEntry,
} from "../../models/cost_center_model";

interface Props {
  data: CostCenter;
  onSubmit: (payload: {
    level: string;
    approval_status: string;
    reason?: string;
    modified_estimate?: number;
  }) => void;
}

const CCRenewalBottomSheet: FunctionComponent<Props> = (props) => {
  const { isAuthorised } = useAuth();

  const currentApprovals = props.data.current_month_approvals ?? [];
  const isFinalised =
    props.data.current_month_status === CCMonthStatus.active ||
    props.data.current_month_status === CCMonthStatus.rejected;

  // Phase 7A: any Finance role (EAF / L1 / L2 / L3 Finance) can approve.
  const isFinance =
    isAuthorised({ module: MODULE.eaf, action: ACTION.UPDATE }) ||
    isAuthorised({ module: MODULE.finance_approval_L1, action: ACTION.UPDATE }) ||
    isAuthorised({ module: MODULE.finance_approval_L2, action: ACTION.UPDATE }) ||
    isAuthorised({ module: MODULE.finance_approval_L3, action: ACTION.UPDATE });

  const currentEntry: RenewalHistoryEntry | undefined = useMemo(() => {
    if (!Array.isArray(props.data.renewal_history)) return undefined;
    return props.data.renewal_history.find(
      (h) => h.month === props.data.current_month,
    );
  }, [props.data.renewal_history, props.data.current_month]);

  const [modifiedEstimate, setModifiedEstimate] = useState<number | "">("");
  useEffect(() => {
    setModifiedEstimate("");
  }, [props.data.current_month, props.data.current_month_status]);

  if (
    props.data.current_month_status !== CCMonthStatus.pendingRenewal &&
    !isFinalised
  )
    return null;
  if (!props.data.business_unit_id) return null;

  const deltaIsNegative = (currentEntry?.delta ?? 0) < 0;
  const recalibratedEstimate =
    (currentEntry?.new_estimate ?? Number(props.data.monthly_estimate)) || 0;

  return (
    <div className="mt-2 border rounded-md p-3 bg-gray-50">
      <div className="pb-4">
        {/* Single-step stepper: Finance only. */}
        <StepperWidget
          steps={[
            {
              label: "Finance",
              validated:
                isFinalised &&
                props.data.current_month_status === CCMonthStatus.active,
              required: false,
            },
          ]}
          activeStep={0}
          onChange={() => {}}
        />
      </div>

      {/* Recalibration summary */}
      {currentEntry && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-white border rounded-md p-3 mb-3">
          <div>
            <div className="text-gray-500">Month</div>
            <div className="font-semibold">{currentEntry.month}</div>
          </div>
          <div>
            <div className="text-gray-500">Prior actual</div>
            <div className="font-semibold">
              ₹{Number(currentEntry.prior_month_actual).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Prior estimate</div>
            <div className="font-semibold">
              ₹{Number(currentEntry.prior_estimate).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Recalibrated estimate</div>
            <div
              className={`font-semibold ${
                deltaIsNegative ? "text-blue-700" : "text-amber-700"
              }`}
            >
              ₹{Number(currentEntry.new_estimate).toLocaleString()}
              <span className="ml-1 text-gray-500 font-normal">
                ({currentEntry.delta >= 0 ? "+" : ""}
                ₹{Number(currentEntry.delta).toLocaleString()})
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="text-xs text-gray-500">
          Renewal status: <b>{props.data.current_month_status}</b>
          {!isFinalised && (
            <>
              {" · "}Awaiting <b>Finance</b>
            </>
          )}
        </div>

        {isFinance && !isFinalised && (
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3 w-full md:w-auto">
            {/* Approve-with-changes: Finance can override the recalibrated estimate */}
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-gray-500">
                Modify estimate (optional)
              </label>
              <input
                type="number"
                data-testid="cc-renewal-modify-estimate"
                placeholder={String(recalibratedEstimate)}
                value={modifiedEstimate}
                onChange={(e) =>
                  setModifiedEstimate(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="border border-gray-200 rounded-md px-2 py-1 text-xs w-40"
              />
            </div>
            <button
              data-testid="cc-renewal-reject"
              onClick={() => {
                const reason =
                  window.prompt("Reason for rejecting this renewal?")?.trim() ||
                  "";
                if (!reason) return;
                if (
                  !window.confirm(`Reject renewal for ${props.data.name}?`)
                )
                  return;
                props.onSubmit({
                  level: "finance",
                  approval_status: MasterCostCenterStatus.rejected,
                  reason,
                });
              }}
              className="w-full sm:w-auto rounded-md border border-myPrimaryColor px-4 py-1.5 text-myPrimaryColor text-sm"
            >
              Reject
            </button>
            <button
              data-testid="cc-renewal-approve"
              onClick={() => {
                const payload: {
                  level: string;
                  approval_status: string;
                  modified_estimate?: number;
                } = {
                  level: "finance",
                  approval_status: MasterCostCenterStatus.approved,
                };
                if (
                  modifiedEstimate !== "" &&
                  Number(modifiedEstimate) !== recalibratedEstimate
                ) {
                  payload.modified_estimate = Number(modifiedEstimate);
                }
                const confirmMsg = payload.modified_estimate
                  ? `Approve renewal with monthly estimate set to ₹${payload.modified_estimate.toLocaleString()}?`
                  : "Approve renewal?";
                if (!window.confirm(confirmMsg)) return;
                props.onSubmit(payload);
              }}
              className="w-full sm:w-auto bg-myPrimaryColor border border-myPrimaryColor rounded-md px-4 py-1.5 text-white text-sm"
            >
              {modifiedEstimate !== "" &&
              Number(modifiedEstimate) !== recalibratedEstimate
                ? "Approve with changes"
                : "Approve"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CCRenewalBottomSheet;
